import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageBar, MessageBarBody, Text } from '@fluentui/react-components';
import Instructions from '../components/Instructions';
import BackToWork from '../components/BackToWork';
import UploadsPanel from '../components/UploadsPanel';
import type { UploadsPanelMode } from '../components/UploadsPanel';
import type { InstructionScreen } from '../lib/instructions';
import { dr15JournalHandoffUrl } from '../lib/journalHandoff';
import { usePageStyles } from '../components/layout/pageStyles';

/**
 * Shared body for the two bank-flow screens (CONVERSIONS_SPEC.md §9). They differ in
 * exactly one thing — which Xero shape a row produces — so they are one component with a
 * mode rather than two near-identical files that drift apart.
 *
 * <p>⚠ <b>One client context per page, carried in `?clientId=`.</b> §9's rule: a screen
 * must be linkable and survive a refresh. `ExportPage` asked for the client twice (once for
 * uploads, once for the journal form) and kept both in component state, so neither could be
 * shared or reloaded.</p>
 */
export function BankFlowScreen({
  mode,
  titleKey,
  subtitleKey,
  noteKey,
  instructionsScreen,
}: {
  mode: Exclude<UploadsPanelMode, 'library'>;
  titleKey: string;
  subtitleKey: string;
  noteKey: string;
  // Passed in rather than derived from `mode`, for the same reason the copy keys are:
  // the two screens are separate products of §9, and a derivation here would be a second
  // place that decides which is which.
  instructionsScreen: InstructionScreen;
}) {
  const frame = usePageStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') ?? '';

  // The client is picked in the HEADER now (docs/client-context.md): one picker, every screen.

  return (
    <div className={frame.page}>
      {/* A visible way back to the client's Work list (Christian's walk, 2026-10-28). */}
      {clientId && <BackToWork clientId={clientId} />}
      <div className={frame.header}>
        <Text as="h1" className={frame.title}>{t(titleKey)}</Text>
        <Text className={frame.subtitle}>{t(subtitleKey)}</Text>
      </div>

      {/* §6: the panel explains the TASK, and it sits above the first control rather than
          below it — a bookkeeper who has never seen the tool should read what this screen
          is for before being asked to pick a client. */}
      <Instructions screen={instructionsScreen} />

      {!clientId && (
        <MessageBar intent="info">
          <MessageBarBody>{t('clientContext.chooseInHeader')}</MessageBarBody>
        </MessageBar>
      )}

      {/* Says which Xero shape this screen produces. The two shapes are not interchangeable
          and the old combined screen never said which was which. */}
      <MessageBar intent="info">
        <MessageBarBody>{t(noteKey)}</MessageBarBody>
      </MessageBar>

      {clientId && (
      <div className="card">
        <UploadsPanel
          clientId={clientId}
          mode={mode}
          // A DR-15 extraction knows which template applies, so it says so.
          // programmatic-nav: after the extraction, the journal it fills (no element is being followed).
          onDr15Extracted={(doc, result) => navigate(dr15JournalHandoffUrl(doc, result))}
        />
      </div>
      )}
    </div>
  );
}

export default function BankTransactionsPage() {
  return (
    <BankFlowScreen
      mode="bankStatement"
      titleKey="bankTransactionsPage.title"
      subtitleKey="bankTransactionsPage.subtitle"
      noteKey="bankTransactionsPage.note"
      instructionsScreen="bankTransactions"
    />
  );
}
